/**
 * Product Variant Selector Web Component
 *
 * Manages color variant links that automatically update based on the currently selected size.
 * When a user changes the size in the size-selector, all color variant links update
 * to point to the same size in the other color products.
 *
 * @requires FoxTheme.pubsub - For size change events
 * @requires window.SectionRenderer - For smooth section morphing on color changes
 */
if (!customElements.get('product-variant-selector')) {
  customElements.define(
    'product-variant-selector',
    class ProductVariantSelector extends HTMLElement {
      abortController = undefined;
      sizeChangeUnsubscriber = undefined;
      variantMap = {};
      currentSize = '';
      useMorphing = true; // Enable morphing for color changes
      isMorphing = false; // Track if a morph is currently in progress
      debug = false; // Enable debug logging

      constructor() {
        super();
      }

      log(...args) {
        if (this.debug) {
          console.log('[ProductVariantSelector]', ...args);
        }
      }

      error(...args) {
        console.error('[ProductVariantSelector]', ...args);
      }

      connectedCallback() {
        this.log('connectedCallback');
        this.abortController = new AbortController();
        this.loadVariantMap();
        this.currentSize = this.dataset.currentSize || '';
        this.useMorphing = this.dataset.useMorphing !== 'false'; // Can be disabled via data attribute
        this.subscribeToSizeChanges();
        this.initClickHandlers();
        this.initPopStateHandler();

        // Initial update of color links based on current size
        if (this.currentSize) {
          this.updateColorLinks(this.currentSize);
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.sizeChangeUnsubscriber?.();
      }

      /**
       * Load and parse the variant map JSON from data attribute
       */
      loadVariantMap() {
        try {
          const variantMapJson = this.dataset.variantMap;
          if (variantMapJson) {
            this.variantMap = JSON.parse(variantMapJson);
          }
        } catch (error) {
          console.error('Error parsing variant map:', error);
          this.variantMap = {};
        }
      }

      /**
       * Initialize click handlers for color links
       * Prevents default link behavior and navigates directly to avoid double page load
       */
      initClickHandlers() {
        const colorLinks = this.querySelectorAll('.product-variant-selector__color-link');
        this.log('initClickHandlers - Found', colorLinks.length, 'color links');

        colorLinks.forEach((link, index) => {
          link.addEventListener(
            'click',
            this.handleColorLinkClick.bind(this),
            { signal: this.abortController.signal }
          );
          this.log(`  Bound listener to link ${index}:`, link.href);
        });
      }

      /**
       * Initialize popstate handler for browser back/forward buttons
       * Enables smooth morphing when navigating through browser history
       */
      initPopStateHandler() {
        window.addEventListener(
          'popstate',
          this.handlePopState.bind(this),
          { signal: this.abortController.signal }
        );
      }

      /**
       * Handle browser back/forward navigation
       * @param {PopStateEvent} event - Popstate event
       */
      async handlePopState(event) {
        // Only handle if we're on a product page with morphing enabled
        if (!this.useMorphing || !window.SectionRenderer) {
          return;
        }

        // Get the current URL (browser has already updated location)
        const currentUrl = window.location.href;

        // Check if this is a product page URL (has /products/ in path)
        if (!currentUrl.includes('/products/')) {
          return;
        }

        // Morph to the URL from browser history
        const productInfo = document.querySelector('product-info');
        if (!productInfo) {
          return;
        }

        const sectionId = productInfo.dataset.section;

        try {
          // Set morphing flag to block rapid navigation
          this.isMorphing = true;

          this.classList.add('variant-selector--loading');

          // Use section renderer to morph to the history state
          await window.SectionRenderer.renderSection(sectionId, {
            url: currentUrl,
            cache: false,
            morphCallbacks: {
              beforeNodeMorphed: (oldNode, newNode) => {
                this.scrollY = window.scrollY;
              },
              afterNodeMorphed: (oldNode, newNode) => {
                // Restore scroll position
                if (this.scrollY !== undefined) {
                  window.scrollTo(0, this.scrollY);
                }

                // Publish variant change event
                const variant = this.extractVariantFromUrl(currentUrl);
                if (variant) {
                  FoxTheme.pubsub?.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
                    data: {
                      sectionId,
                      variant,
                    },
                  });

                  document.dispatchEvent(
                    new CustomEvent('variant:changed', {
                      detail: { variant },
                    })
                  );
                }
              },
            },
          });

          // Re-initialize Web Components after morphing
          const productSection = document.getElementById(`shopify-section-${sectionId}`);
          if (productSection) {
            await new Promise(resolve => setTimeout(resolve, 0));

            this.reinitializeWebComponents(productSection);

            // Update current size and variant map
            const newVariantSelector = productSection.querySelector('product-variant-selector');
            if (newVariantSelector) {
              this.currentSize = newVariantSelector.dataset.currentSize || '';
              this.loadVariantMap();
            }

            // Trigger change event on variant input for Shopify Analytics & third-party scripts
            this.triggerVariantInputChange(productSection, currentUrl);

            // CRITICAL: Update color links and re-bind event listeners AFTER DOM morphing is complete
            // Double requestAnimationFrame ensures we run after all DOM changes
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (newVariantSelector) {
                  this.updateColorLinks(this.currentSize);
                }

                // Re-bind event listeners for color links after morph
                if (newVariantSelector && newVariantSelector !== this) {
                  newVariantSelector.abortController = new AbortController();
                  newVariantSelector.initClickHandlers();
                  newVariantSelector.initPopStateHandler();
                } else {
                  this.abortController.abort();
                  this.abortController = new AbortController();
                  this.initClickHandlers();
                  this.initPopStateHandler();
                }
              });
            });
          }
        } catch (error) {
          console.error('Error morphing from popstate:', error);
          // On error, let browser do full page reload
          window.location.reload();
        } finally {
          this.classList.remove('variant-selector--loading');
          // Reset morphing flag to allow new clicks
          this.isMorphing = false;
        }
      }

      /**
       * Handle click on color variant link
       * @param {Event} event - Click event
       */
      async handleColorLinkClick(event) {
        const link = event.currentTarget;
        this.log('🖱️ Click on color link:', link.href);
        this.log('  isMorphing:', this.isMorphing);
        this.log('  currentSize:', this.currentSize);
        this.log('  Link attributes:', {
          'aria-disabled': link.getAttribute('aria-disabled'),
          'aria-current': link.getAttribute('aria-current'),
          'class': link.className,
          'data-product-id': link.dataset.productId
        });

        // Check if link is disabled (unavailable variant)
        if (link.getAttribute('aria-disabled') === 'true') {
          this.log('  ❌ Link is disabled');
          event.preventDefault();
          return;
        }

        // IMPORTANT: Block clicks while morphing is in progress
        if (this.isMorphing) {
          this.log('  ⏸️ Blocked: morphing in progress');
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }

        // Check if we have a size selected and a variant URL
        if (this.currentSize && link.href) {
          this.log('  ✅ Proceeding with morph');
          // Prevent default link navigation
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          // Use morphing if available and enabled
          if (this.useMorphing && window.SectionRenderer) {
            await this.morphToVariant(link.href);
          } else {
            // Fallback to direct navigation
            this.log('  Fallback to navigation');
            window.location.href = link.href;
          }
        } else {
          this.log('  ⚠️ No size selected or no href');
        }
        // If no size selected or no href, let the default link behavior happen
      }

      /**
       * Morph to a new variant without page reload
       * @param {string} variantUrl - URL of the variant to morph to
       */
      async morphToVariant(variantUrl) {
        this.log('🔄 morphToVariant START:', variantUrl);

        // Find the product-info element to get section ID
        const productInfo = document.querySelector('product-info');
        if (!productInfo) {
          this.error('❌ product-info not found, falling back to navigation');
          window.location.href = variantUrl;
          return;
        }

        const sectionId = productInfo.dataset.section;
        this.log('  sectionId:', sectionId);

        try {
          // Set morphing flag to block rapid clicks
          this.log('  Setting isMorphing = true');
          this.isMorphing = true;

          // Show loading state
          this.classList.add('variant-selector--loading');

          this.log('  Calling SectionRenderer.renderSection...');
          // Use section renderer to morph the section smoothly
          await window.SectionRenderer.renderSection(sectionId, {
            url: variantUrl,
            cache: false, // Don't cache variant-specific renders
            morphCallbacks: {
              beforeNodeMorphed: (oldNode, newNode) => {
                // Preserve scroll position
                this.scrollY = window.scrollY;
              },
              afterNodeMorphed: (oldNode, newNode) => {
                // Restore scroll position
                if (this.scrollY !== undefined) {
                  window.scrollTo(0, this.scrollY);
                }

                // Publish variant change event
                const variant = this.extractVariantFromUrl(variantUrl);
                if (variant) {
                  FoxTheme.pubsub?.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
                    data: {
                      sectionId,
                      variant,
                    },
                  });

                  document.dispatchEvent(
                    new CustomEvent('variant:changed', {
                      detail: { variant },
                    })
                  );
                }
              },
            },
          });

          this.log('  ✅ Section morphing complete');

          // IMPORTANT: Update URL in browser history AFTER morphing is complete
          // This creates a history entry that can be navigated with back/forward buttons
          window.history.pushState({ variantUrl }, '', variantUrl);
          this.log('  Updated URL in browser history');

          // IMPORTANT: Re-initialize Web Components AFTER morphing is complete
          // Doing this inside afterNodeMorphed can cause race conditions if called multiple times
          const productSection = document.getElementById(`shopify-section-${sectionId}`);
          if (productSection) {
            this.log('  Found product section');
            // Small delay to ensure DOM is fully updated and custom elements are upgraded
            await new Promise(resolve => setTimeout(resolve, 0));

            this.log('  Reinitializing web components...');
            this.reinitializeWebComponents(productSection);

            // Update current size and variant map from new content
            const newVariantSelector = productSection.querySelector('product-variant-selector');
            this.log('  newVariantSelector found:', !!newVariantSelector);
            this.log('  newVariantSelector === this:', newVariantSelector === this);

            if (newVariantSelector) {
              this.currentSize = newVariantSelector.dataset.currentSize || '';
              this.loadVariantMap();
              this.log('  Updated currentSize to:', this.currentSize);
            }

            // IMPORTANT: Trigger change event on variant input for Shopify Analytics & third-party scripts
            this.triggerVariantInputChange(productSection, variantUrl);

            // CRITICAL: Update color links and re-bind event listeners AFTER DOM morphing is complete
            // We need double requestAnimationFrame to ensure we run after all DOM changes
            // Single RAF is not enough - the morph can still overwrite our changes
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.log('  ⏰ Updating color links after morph (double RAF)...');
                if (newVariantSelector) {
                  this.updateColorLinks(this.currentSize);
                }

                // Re-bind event listeners for color links after morph
                // The DOM elements have been replaced, so old event listeners are orphaned
                if (newVariantSelector && newVariantSelector !== this) {
                  this.log('  🔁 Re-binding listeners on NEW selector element');
                  // If a new product-variant-selector element was created, rebind its listeners
                  newVariantSelector.abortController = new AbortController();
                  newVariantSelector.initClickHandlers();
                  newVariantSelector.initPopStateHandler();
                } else {
                  this.log('  🔁 Re-binding listeners on SAME selector element (this)');
                  // If it's the same element (which it should be), just rebind the listeners
                  this.abortController.abort();
                  this.abortController = new AbortController();
                  this.initClickHandlers();
                  this.initPopStateHandler();
                }
              });
            });
          } else {
            this.error('  ❌ Product section not found!');
          }
        } catch (error) {
          this.error('❌ Error morphing to variant:', error);
          // Fallback to navigation on error
          window.location.href = variantUrl;
        } finally {
          this.classList.remove('variant-selector--loading');
          // Reset morphing flag to allow new clicks
          this.log('  Setting isMorphing = false');
          this.isMorphing = false;
          this.log('🔄 morphToVariant END');
        }
      }

      /**
       * Extract variant ID from URL
       * @param {string} url - URL with variant parameter
       * @returns {Object|null} - Variant object with id
       */
      extractVariantFromUrl(url) {
        try {
          const urlObj = new URL(url, window.location.origin);
          const variantId = urlObj.searchParams.get('variant');
          return variantId ? { id: parseInt(variantId) } : null;
        } catch (e) {
          return null;
        }
      }

      /**
       * Re-initialize Web Components after morphing
       * This ensures components like size-selector work correctly after morph
       * @param {HTMLElement} container - The morphed container element
       */
      reinitializeWebComponents(container) {
        // Find all size-selector elements in the morphed content
        const sizeSelectors = container.querySelectorAll('size-selector');

        sizeSelectors.forEach(selector => {
          // Call refresh() method if available (preferred method)
          if (selector.refresh && typeof selector.refresh === 'function') {
            selector.refresh();
          }
          // Fallback: manually trigger connectedCallback if refresh not available
          else if (selector.connectedCallback && typeof selector.connectedCallback === 'function') {
            if (!selector.abortController) {
              selector.connectedCallback();
            }
          }
        });

        // Also handle product-info components
        const productInfos = container.querySelectorAll('product-info');
        productInfos.forEach(info => {
          // Only re-initialize if not already initialized
          if (info.connectedCallback && typeof info.connectedCallback === 'function' && !info.onVariantChangeUnsubscriber) {
            info.connectedCallback();
          }
        });

        // CRITICAL: Handle media-gallery components
        // After morphing, the gallery's Swiper instances need to be reinitialized
        const mediaGalleries = container.querySelectorAll('media-gallery');
        mediaGalleries.forEach(gallery => {
          this.log('  Reinitializing media-gallery:', gallery);

          // Destroy existing Swiper instances to prevent conflicts
          if (gallery.sliderInstance && typeof gallery.sliderInstance === 'object') {
            this.log('    Destroying existing slider instance');
            gallery.sliderInstance.slider?.destroy();
            gallery.sliderInstance = false;
          }

          if (gallery.thumbsInstance && typeof gallery.thumbsInstance === 'object') {
            this.log('    Destroying existing thumbs instance');
            gallery.thumbsInstance.slider?.destroy();
            gallery.thumbsInstance = false;
          }

          // Destroy and reinitialize PhotoSwipe lightbox if present
          if (gallery.lightbox) {
            this.log('    Destroying existing lightbox');
            gallery.lightbox.destroy();
            gallery.lightbox = null;
          }

          // Re-initialize the gallery
          if (gallery.init && typeof gallery.init === 'function') {
            this.log('    Calling gallery.init()');
            gallery.init();
          }
        });

        // Force reload of lazy-loaded images after morph
        this.refreshLazyImages(container);
      }

      /**
       * Subscribe to size change events from size-selector
       */
      subscribeToSizeChanges() {
        if (!window.FoxTheme || !window.FoxTheme.pubsub) {
          console.warn('FoxTheme.pubsub not available for product-variant-selector');
          return;
        }

        this.sizeChangeUnsubscriber = window.FoxTheme.pubsub.subscribe(
          window.FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleSizeChange.bind(this)
        );
      }

      /**
       * Handle size change event from size-selector
       * @param {Object} event - Published event with size information
       */
      handleSizeChange(event) {
        this.log('📏 handleSizeChange triggered');
        const { data } = event;

        this.log('  Event data:', data);

        // Check if this event is from a size-selector in our product
        if (!data || !data.selectedSize) {
          this.log('  ⚠️ No data or selectedSize, ignoring');
          return;
        }

        const newSize = data.selectedSize;
        this.log('  newSize:', newSize, 'currentSize:', this.currentSize);

        // Only update if size actually changed
        if (newSize !== this.currentSize) {
          this.log('  ✅ Size changed, updating color links');
          this.currentSize = newSize;
          this.updateColorLinks(newSize);
        } else {
          this.log('  ℹ️ Size unchanged, skipping update');
        }
      }

      /**
       * Update all color variant links to point to the specified size
       * @param {string} size - The size to link to (e.g., "43", "44")
       */
      updateColorLinks(size) {
        this.log('🔗 updateColorLinks called with size:', size);
        this.log('  Current variantMap:', this.variantMap);

        const colorLinks = this.querySelectorAll('.product-variant-selector__color-link');
        this.log('  Found', colorLinks.length, 'color links');

        colorLinks.forEach((link, index) => {
          const productId = link.dataset.productId;
          const baseUrl = link.dataset.baseUrl;

          this.log(`  Link ${index}:`, {
            productId,
            baseUrl,
            currentHref: link.href
          });

          if (!productId || !baseUrl) {
            this.log(`    ⚠️ Missing productId or baseUrl`);
            return;
          }

          // Find the variant for this product + size combination
          const variantUrl = this.findVariantUrlBySize(productId, size);
          this.log(`    variantUrl found:`, variantUrl);

          if (variantUrl) {
            // Update link to variant URL
            link.href = variantUrl;
            link.removeAttribute('aria-disabled');
            link.classList.remove('product-variant-selector__color-link--unavailable');
            this.log(`    ✅ Updated to variant URL, removed disabled state`);
          } else {
            // Size not available for this color - fallback to base URL
            link.href = baseUrl;
            this.log(`    ⚠️ No variantUrl found, using base URL`);

            // Optionally mark as unavailable if we have the variant data
            const productVariants = this.variantMap[productId];
            this.log(`    productVariants in map:`, productVariants);

            if (productVariants && productVariants.variants) {
              const hasSize = productVariants.variants.some(v => v.size === size);
              this.log(`    hasSize ${size}:`, hasSize);

              if (hasSize) {
                // Size exists but not available
                link.setAttribute('aria-disabled', 'true');
                link.classList.add('product-variant-selector__color-link--unavailable');
                this.log(`    ❌ Marked as UNAVAILABLE (size exists but not available)`);
              } else {
                this.log(`    ℹ️ Size doesn't exist for this product, keeping link enabled`);
              }
            }
          }
        });
      }

      /**
       * Find the variant URL for a specific product and size
       * @param {string} productId - The product ID
       * @param {string} size - The size to find
       * @returns {string|null} - The variant URL or null if not found
       */
      findVariantUrlBySize(productId, size) {
        this.log(`    🔍 findVariantUrlBySize(productId: ${productId}, size: ${size})`);

        const productVariants = this.variantMap[productId];

        if (!productVariants || !productVariants.variants) {
          this.log(`      ❌ No variants found in map for productId ${productId}`);
          return null;
        }

        this.log(`      Found ${productVariants.variants.length} variants:`, productVariants.variants);

        // Find variant with matching size that is available
        const variant = productVariants.variants.find(v => {
          return v.size === size && v.available === true;
        });

        if (variant) {
          this.log(`      ✅ Found available variant:`, variant);
          return variant.url;
        }

        // If exact match not found, try to find any variant with this size (even if unavailable)
        const unavailableVariant = productVariants.variants.find(v => v.size === size);

        if (unavailableVariant) {
          this.log(`      ⚠️ Found unavailable variant:`, unavailableVariant);
          return unavailableVariant.url;
        }

        this.log(`      ❌ No variant found for size ${size}`);
        return null;
      }

      /**
       * Trigger change event on hidden variant input
       * This ensures Shopify Analytics and third-party scripts are notified
       * @param {HTMLElement} container - The product section container
       * @param {string} url - The variant URL to extract variant ID from
       */
      triggerVariantInputChange(container, url) {
        const variant = this.extractVariantFromUrl(url);
        if (!variant || !variant.id) return;

        // Find all product forms in the section
        const productForms = container.querySelectorAll('form[action*="/cart/add"]');

        productForms.forEach(form => {
          const variantInput = form.querySelector('input[name="id"]');
          if (variantInput) {
            // Update the variant ID
            variantInput.value = variant.id;

            // Enable/disable input based on whether we have a valid variant
            if (variant.id) {
              variantInput.removeAttribute('disabled');
            } else {
              variantInput.setAttribute('disabled', '');
            }

            // Dispatch change event for Analytics and third-party scripts
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      /**
       * Refresh lazy-loaded images after morphing
       * This ensures images load properly after DOM changes
       * @param {HTMLElement} container - The morphed container element
       */
      refreshLazyImages(container) {
        this.log('  Refreshing lazy-loaded images...');

        // Find all images with loading="lazy" or data-src attributes
        const lazyImages = container.querySelectorAll('img[loading="lazy"], img[data-src], [is="image-lazy"]');

        lazyImages.forEach((img, index) => {
          // If image has data-src, load it immediately
          if (img.dataset.src && !img.src) {
            this.log(`    Loading image ${index} from data-src:`, img.dataset.src);
            img.src = img.dataset.src;
          }

          // Force browser to re-evaluate lazy loading by temporarily removing and re-adding loading attribute
          if (img.hasAttribute('loading')) {
            const loadingValue = img.getAttribute('loading');
            img.removeAttribute('loading');

            // Use RAF to ensure DOM update
            requestAnimationFrame(() => {
              img.setAttribute('loading', loadingValue);

              // If image is in viewport, load it immediately
              const rect = img.getBoundingClientRect();
              const isInViewport = (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth)
              );

              if (isInViewport && img.dataset.src && !img.src) {
                this.log(`    Image ${index} in viewport, loading immediately`);
                img.src = img.dataset.src;
              }
            });
          }

          // Trigger connectedCallback for custom image elements
          if (img.tagName.toLowerCase().includes('-') || img.getAttribute('is')) {
            if (img.connectedCallback && typeof img.connectedCallback === 'function') {
              this.log(`    Re-initializing custom image element ${index}`);
              img.connectedCallback();
            }
          }
        });

        // Also handle picture elements
        const pictures = container.querySelectorAll('picture');
        pictures.forEach((picture, index) => {
          const sources = picture.querySelectorAll('source[data-srcset]');
          sources.forEach(source => {
            if (source.dataset.srcset && !source.srcset) {
              this.log(`    Loading source ${index} from data-srcset`);
              source.srcset = source.dataset.srcset;
            }
          });
        });

        this.log('  ✅ Lazy image refresh complete');
      }

      /**
       * Get the current selected size
       * @returns {string} - Current size
       */
      getCurrentSize() {
        return this.currentSize;
      }

      /**
       * Manually set the current size and update links
       * @param {string} size - Size to set
       */
      setCurrentSize(size) {
        if (size !== this.currentSize) {
          this.currentSize = size;
          this.updateColorLinks(size);
        }
      }
    }
  );
}
